import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, useWindowDimensions } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useQuery } from '@tanstack/react-query';
import { MaterialIcons, FontAwesome5, Ionicons } from '@expo/vector-icons';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import Chart from '@components/dataTables/chart';
import DataChart from '@components/dataTables/DataChart';
import { Card } from '@constants/Themed';
import { H4, H5, XStack, YStack } from 'tamagui';
import moment from 'moment';

interface CRMAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

const CRMAnalytics: React.FC<CRMAnalyticsProps> = ({ dateRange }) => {
  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const [selectedMetric, setSelectedMetric] = useState<'overview' | 'pipeline' | 'contacts' | 'performance'>('overview');

  // Theme-aware styles
  const cardStyle = useMemo(() => getModernCardStyle(theme), [theme]);
  const largeCardStyle = useMemo(() => getModernCardStyle(theme, 'large'), [theme]);
  const textColor = useMemo(() => getModernTextColor(theme), [theme]);
  const secondaryTextColor = useMemo(() => getModernTextColor(theme, 'secondary'), [theme]);
  const mutedTextColor = useMemo(() => getModernTextColor(theme, 'muted'), [theme]);

  // CRM Clients Query
  const { data: crmClientsData, isLoading: crmLoading } = useQuery({
    queryKey: ['crmClients'],
    queryFn: async () => {
      // Use sample data for CRM analytics since getCrmItems needs parameters
      const sampleCRMData = [
        {
          _id: '1',
          name: 'John Smith',
          company: 'Tech Corp',
          email: 'john@techcorp.com',
          phone: '+1-555-123-4567',
          status: 'lead',
          leadScore: 75,
          dealValue: 25000,
          lastContactDate: new Date('2024-01-15'),
        },
        {
          _id: '2',
          name: 'Sarah Johnson',
          company: 'Design Studio',
          email: 'sarah@designstudio.com',
          phone: '+1-555-987-6543',
          status: 'client',
          leadScore: 90,
          dealValue: 50000,
          lastContactDate: new Date('2024-01-18'),
        },
        {
          _id: '3',
          name: 'Mike Wilson',
          company: 'StartupX',
          email: 'mike@startupx.com',
          phone: '+1-555-456-7890',
          status: 'prospect',
          leadScore: 60,
          dealValue: 15000,
          lastContactDate: new Date('2024-01-10'),
        },
        {
          _id: '4',
          name: 'Emily Davis',
          company: 'Global Solutions',
          email: 'emily@globalsolutions.com',
          phone: '+1-555-321-0987',
          status: 'lead',
          leadScore: 85,
          dealValue: 35000,
          lastContactDate: new Date('2024-01-20'),
        },
        {
          _id: '5',
          name: 'David Brown',
          company: 'Innovation Labs',
          email: 'david@innovationlabs.com',
          phone: '+1-555-654-3210',
          status: 'client',
          leadScore: 95,
          dealValue: 75000,
          lastContactDate: new Date('2024-01-22'),
        }
      ];
      return sampleCRMData;
    },
    staleTime: 3 * 60 * 1000,
  });

  // CRM Folders Query
  const { data: crmFoldersData, isLoading: foldersLoading } = useQuery({
    queryKey: ['crmFolders'],
    queryFn: async () => {
      const sampleFolders = [
        { _id: '1', name: 'Hot Leads', count: 15 },
        { _id: '2', name: 'Active Clients', count: 8 },
        { _id: '3', name: 'Cold Prospects', count: 22 },
        { _id: '4', name: 'Lost Opportunities', count: 5 }
      ];
      return sampleFolders;
    },
    staleTime: 3 * 60 * 1000,
  });

  // Timeline Data Query for multiple contacts
  const { data: timelineData, isLoading: timelineLoading } = useQuery({
    queryKey: ['crmTimelineData'],
    queryFn: async () => {
      if (!crmClientsData || crmClientsData.length === 0) return [];
      
      // Generate sample timeline data
      const sampleTimelines = crmClientsData.slice(0, 5).map((client) => ({
        clientId: client._id,
        client,
        data: {
          metrics: {
            totalInteractions: Math.floor(Math.random() * 20) + 5,
            avgResponseTime: Math.floor(Math.random() * 24) + 1,
            lastContactDate: client.lastContactDate,
            dealValue: client.dealValue,
            leadScore: client.leadScore
          },
          timelineActivities: [
            {
              type: 'email',
              description: 'Sent proposal email',
              timestamp: new Date('2024-01-20'),
              author: 'Sales Team'
            },
            {
              type: 'call',
              description: 'Follow-up call scheduled',
              timestamp: new Date('2024-01-18'),
              author: 'Account Manager'
            }
          ]
        }
      }));
      
      return sampleTimelines;
    },
    enabled: !!crmClientsData && crmClientsData.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Metric Selector
  const MetricSelector = () => (
    <View style={[cardStyle, { marginHorizontal: 20, marginBottom: 16, flexDirection: 'row' }]}>
      {[
        { key: 'overview', label: 'Overview', icon: 'dashboard' },
        { key: 'pipeline', label: 'Pipeline', icon: 'trending-up' },
        { key: 'contacts', label: 'Contacts', icon: 'people' },
        { key: 'performance', label: 'Performance', icon: 'analytics' }
      ].map((metric) => (
        <Pressable
          key={metric.key}
          onPress={() => setSelectedMetric(metric.key as any)}
          style={[
            styles.modernButton,
            getModernActionStyle(theme, selectedMetric === metric.key ? 'blue' : 'grey'),
            { 
              flex: 1, 
              marginHorizontal: 2, 
              paddingVertical: 10,
              flexDirection: 'row',
              alignItems: 'center', 
              justifyContent: 'center' 
            }
          ]}
        >
          <MaterialIcons 
            name={metric.icon as any} 
            size={16} 
            color={selectedMetric === metric.key ? '#ffffff' : mutedTextColor} 
          />
          <Text style={[{
            color: selectedMetric === metric.key ? '#ffffff' : mutedTextColor,
            fontSize: 12,
            fontWeight: '600',
            marginLeft: 4
          }]}>
            {metric.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );

  // CRM Metric Card
  const CRMMetricCard = ({ title, value, change, icon, color = 'blue' as 'blue' | 'green' | 'red' | 'amber' | 'grey', subtitle = '' }) => (
    <Animatable.View animation="fadeInUp" duration={600} style={[cardStyle, { flex: 1, margin: 4 }]}>
      <View style={{ padding: 16 }}>
        <View style={styles.modernRow}>
          <View style={[styles.modernActionButton, getModernActionStyle(theme, color)]}>
            {icon}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[{ color: mutedTextColor, fontSize: 12, marginBottom: 4 }]}>{title}</Text>
            <Text style={[{ color: textColor, fontSize: 24, fontWeight: '700' }]}>{value}</Text>
            {subtitle && (
              <Text style={[{ color: secondaryTextColor, fontSize: 11, marginTop: 2 }]}>{subtitle}</Text>
            )}
            {change && (
              <Text style={[{ 
                fontSize: 12, 
                color: change.startsWith('+') ? '#22c55e' : change.startsWith('-') ? '#ef4444' : mutedTextColor,
                marginTop: 4
              }]}>
                {change}
              </Text>
            )}
          </View>
        </View>
      </View>
    </Animatable.View>
  );

  // Overview Section
  const renderOverview = () => {
    if (!crmClientsData) return null;

    const clients = crmClientsData || [];
    const leads = clients.filter(c => c.status === 'lead' || c.leadScore > 0);
    const activeClients = clients.filter(c => c.status === 'client' || c.status === 'active');
    const prospects = clients.filter(c => c.status === 'prospect');
    
    const totalDealValue = clients.reduce((sum, c) => sum + (c.dealValue || 0), 0);
    const avgDealValue = activeClients.length > 0 ? totalDealValue / activeClients.length : 0;

    return (
      <>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16 }}>
          <CRMMetricCard
            title="Total Contacts"
            value={clients.length}
            change="+12 this month"
            icon={<MaterialIcons name="people" size={20} color="#ffffff" />}
            color="blue"
            subtitle="All contacts"
          />
          <CRMMetricCard
            title="Active Leads"
            value={leads.length}
            change="+5 this week"
            icon={<MaterialIcons name="trending-up" size={20} color="#ffffff" />}
            color="green"
            subtitle="Potential customers"
          />
          <CRMMetricCard
            title="Clients"
            value={activeClients.length}
            change="+3 converted"
            icon={<MaterialIcons name="check-circle" size={20} color="#ffffff" />}
            color="green"
            subtitle="Active customers"
          />
          <CRMMetricCard
            title="Pipeline Value"
            value={`$${totalDealValue.toLocaleString()}`}
            change="+18%"
            icon={<MaterialIcons name="attach-money" size={20} color="#ffffff" />}
            color="amber"
            subtitle={`Avg: $${Math.round(avgDealValue).toLocaleString()}`}
          />
        </View>

        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Contact Status Distribution
          </Text>
          
          {[
            { status: 'Leads', count: leads.length, color: '#3b82f6' },
            { status: 'Prospects', count: prospects.length, color: '#f59e0b' },
            { status: 'Active Clients', count: activeClients.length, color: '#10b981' },
            { status: 'Others', count: clients.length - leads.length - prospects.length - activeClients.length, color: '#6b7280' }
          ].map((item, index) => {
            const percentage = clients.length > 0 ? (item.count / clients.length * 100) : 0;
            
            return (
              <View key={index} style={[styles.modernRow, { marginBottom: 12 }]}>
                <View style={[{ width: 16, height: 16, borderRadius: 8, backgroundColor: item.color, marginRight: 12 }]} />
                <Text style={[{ color: textColor, flex: 1, fontSize: 14 }]}>{item.status}</Text>
                <Text style={[{ color: secondaryTextColor, fontSize: 14, marginRight: 12 }]}>{item.count}</Text>
                <Text style={[{ color: mutedTextColor, fontSize: 12, minWidth: 40 }]}>{percentage.toFixed(1)}%</Text>
              </View>
            );
          })}
        </View>
      </>
    );
  };

  // Pipeline Section
  const renderPipeline = () => {
    if (!crmClientsData) return null;

    const clients = crmClientsData || [];
    const pipelineStages = [
      { stage: 'New Lead', filter: (c) => c.status === 'lead' && !c.lastContactDate },
      { stage: 'Contacted', filter: (c) => c.status === 'lead' && c.lastContactDate },
      { stage: 'Qualified', filter: (c) => c.status === 'prospect' || c.leadScore > 50 },
      { stage: 'Proposal', filter: (c) => c.status === 'proposal' || (c.dealValue && c.dealValue > 0) },
      { stage: 'Closed Won', filter: (c) => c.status === 'client' || c.status === 'active' },
      { stage: 'Closed Lost', filter: (c) => c.status === 'lost' || c.status === 'rejected' }
    ];

    return (
      <>
        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Sales Pipeline
          </Text>
          
          {pipelineStages.map((stage, index) => {
            const stageClients = clients.filter(stage.filter);
            const stageValue = stageClients.reduce((sum, c) => sum + (c.dealValue || 0), 0);
            
            return (
              <View key={index} style={[{ marginBottom: 16, padding: 16, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 12 }]}>
                <View style={[styles.modernRow, { marginBottom: 8 }]}>
                  <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', flex: 1 }]}>{stage.stage}</Text>
                  <Text style={[{ color: secondaryTextColor, fontSize: 14 }]}>{stageClients.length} contacts</Text>
                </View>
                <Text style={[{ color: mutedTextColor, fontSize: 12, marginBottom: 8 }]}>
                  Pipeline Value: ${stageValue.toLocaleString()}
                </Text>
                
                {stageClients.slice(0, 3).map((client, clientIndex) => (
                  <View key={clientIndex} style={[styles.modernRow, { marginBottom: 4, paddingLeft: 12 }]}>
                    <View style={[{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#3b82f6', marginRight: 8, marginTop: 4 }]} />
                    <Text style={[{ color: textColor, flex: 1, fontSize: 13 }]}>
                      {client.name || client.company || 'Unknown Contact'}
                    </Text>
                    {client.dealValue > 0 && (
                      <Text style={[{ color: secondaryTextColor, fontSize: 12 }]}>
                        ${client.dealValue.toLocaleString()}
                      </Text>
                    )}
                  </View>
                ))}
                
                {stageClients.length > 3 && (
                  <Text style={[{ color: mutedTextColor, fontSize: 11, paddingLeft: 20, marginTop: 4 }]}>
                    +{stageClients.length - 3} more
                  </Text>
                )}
              </View>
            );
          })}
        </View>
      </>
    );
  };

  // Contacts Section
  const renderContacts = () => {
    if (!crmClientsData) return null;

    const clients = crmClientsData || [];
    const recentContacts = clients
      .filter(c => c.lastContactDate)
      .sort((a, b) => new Date(b.lastContactDate).getTime() - new Date(a.lastContactDate).getTime())
      .slice(0, 10);

    const highValueContacts = clients
      .filter(c => c.dealValue && c.dealValue > 0)
      .sort((a, b) => (b.dealValue || 0) - (a.dealValue || 0))
      .slice(0, 8);

    return (
      <>
        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Recent Contact Activity
          </Text>
          
          {recentContacts.map((contact, index) => (
            <View key={index} style={[styles.modernRow, { marginBottom: 12, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }]}>
              <View style={[{ 
                width: 40, 
                height: 40, 
                borderRadius: 20, 
                backgroundColor: '#3b82f6', 
                justifyContent: 'center', 
                alignItems: 'center',
                marginRight: 12 
              }]}>
                <Text style={[{ color: '#ffffff', fontSize: 16, fontWeight: '700' }]}>
                  {(contact.name || contact.company || 'U').charAt(0).toUpperCase()}
                </Text>
              </View>
              
              <View style={{ flex: 1 }}>
                <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600' }]}>
                  {contact.name || contact.company || 'Unknown Contact'}
                </Text>
                <Text style={[{ color: secondaryTextColor, fontSize: 12, marginTop: 2 }]}>
                  {contact.email || contact.phone || 'No contact info'}
                </Text>
                <Text style={[{ color: mutedTextColor, fontSize: 11, marginTop: 2 }]}>
                  Last contact: {moment(contact.lastContactDate).fromNow()}
                </Text>
              </View>
              
              <View style={[styles.modernActionButton, getModernActionStyle(theme, 
                contact.status === 'client' ? 'green' : 
                  contact.status === 'lead' ? 'amber' : 'grey'
              ), { paddingHorizontal: 8, paddingVertical: 4 }]}>
                <Text style={[{ color: '#ffffff', fontSize: 10, fontWeight: '600' }]}>
                  {contact.status?.toUpperCase() || 'UNKNOWN'}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {highValueContacts.length > 0 && (
          <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
            <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
              High-Value Contacts
            </Text>
            
            {highValueContacts.map((contact, index) => (
              <View key={index} style={[styles.modernRow, { marginBottom: 12, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }]}>
                <View style={{ flex: 1 }}>
                  <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600' }]}>
                    {contact.name || contact.company || 'Unknown Contact'}
                  </Text>
                  <Text style={[{ color: secondaryTextColor, fontSize: 12, marginTop: 2 }]}>
                    Deal Value: ${contact.dealValue?.toLocaleString() || '0'}
                  </Text>
                  <Text style={[{ color: mutedTextColor, fontSize: 11, marginTop: 2 }]}>
                    Score: {contact.leadScore || 'N/A'} • Status: {contact.status || 'Unknown'}
                  </Text>
                </View>
                
                <MaterialIcons name="star" size={20} color="#f59e0b" />
              </View>
            ))}
          </View>
        )}
      </>
    );
  };

  // Performance Section
  const renderPerformance = () => {
    if (!crmClientsData || !timelineData) return null;

    const clients = crmClientsData || [];
    const timelines = timelineData || [];
    
    // Calculate performance metrics
    const totalInteractions = timelines.reduce((sum, t) => sum + (t.data.metrics?.totalInteractions || 0), 0);
    const avgInteractions = timelines.length > 0 ? totalInteractions / timelines.length : 0;
    
    const conversionRate = clients.length > 0 ? 
      (clients.filter(c => c.status === 'client').length / clients.length * 100) : 0;

    const responseTimes = timelines
      .filter(t => t.data.metrics?.avgResponseTime)
      .map(t => t.data.metrics.avgResponseTime);
    
    const avgResponseTime = responseTimes.length > 0 ? 
      responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length : 0;

    return (
      <>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16 }}>
          <CRMMetricCard
            title="Conversion Rate"
            value={`${conversionRate.toFixed(1)}%`}
            change="+2.3%"
            icon={<MaterialIcons name="trending-up" size={20} color="#ffffff" />}
            color="green"
            subtitle="Lead to client"
          />
          <CRMMetricCard
            title="Avg Interactions"
            value={avgInteractions.toFixed(1)}
            change="+0.8"
            icon={<MaterialIcons name="chat" size={20} color="#ffffff" />}
            color="blue"
            subtitle="Per contact"
          />
          <CRMMetricCard
            title="Response Time"
            value={`${Math.round(avgResponseTime)}h`}
            change="-2h"
            icon={<MaterialIcons name="access-time" size={20} color="#ffffff" />}
            color="amber"
            subtitle="Average"
          />
          <CRMMetricCard
            title="Active Deals"
            value={clients.filter(c => c.dealValue > 0).length}
            change="+4"
            icon={<MaterialIcons name="handshake" size={20} color="#ffffff" />}
            color="blue"
            subtitle="In pipeline"
          />
        </View>

        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Contact Engagement Timeline
          </Text>
          
          {timelines.slice(0, 6).map((timeline, index) => {
            const metrics = timeline.data?.metrics || {};
            const activities = timeline.data?.timelineActivities || [];
            
            return (
              <View key={index} style={[{ marginBottom: 16, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }]}>
                <View style={[styles.modernRow, { marginBottom: 8 }]}>
                  <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600', flex: 1 }]}>
                    {timeline.client.name || timeline.client.company || 'Unknown Contact'}
                  </Text>
                  <Text style={[{ color: secondaryTextColor, fontSize: 12 }]}>
                    {activities.length} activities
                  </Text>
                </View>
                
                <View style={[styles.modernRow, { marginBottom: 4 }]}>
                  <Text style={[{ color: mutedTextColor, fontSize: 12, flex: 1 }]}>
                    Interactions: {(metrics as any).totalInteractions || 0}
                  </Text>
                  <Text style={[{ color: mutedTextColor, fontSize: 12 }]}>
                    Score: {timeline.client.leadScore || 'N/A'}
                  </Text>
                </View>
                
                {activities.slice(0, 2).map((activity, actIndex) => (
                  <Text key={actIndex} style={[{ color: mutedTextColor, fontSize: 11, marginTop: 2 }]}>
                    • {activity.type || 'Activity'}: {activity.description || 'No description'}
                  </Text>
                ))}
              </View>
            );
          })}
        </View>
      </>
    );
  };

  return (
    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
      <MetricSelector />
      
      <Animatable.View animation="fadeIn" duration={400}>
        {selectedMetric === 'overview' && renderOverview()}
        {selectedMetric === 'pipeline' && renderPipeline()}
        {selectedMetric === 'contacts' && renderContacts()}
        {selectedMetric === 'performance' && renderPerformance()}
      </Animatable.View>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

export default CRMAnalytics;
